import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Cascader, Form, Input, Select } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import dayjs, { type Dayjs } from "dayjs";
import type {
    ProductOperationVo,
    ProductPagingParams,
    ProductStatisticAllParams,
    ProductStatisticAllVo
} from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useRef, useState, type FocusEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { PRODUCT_TYPE } from "@zhihaoscm/meta";
import { formatNumberWithUnitAndChinese, formartTimeWithUnitAndChinese } from "@zhihaoscm/utils";
import {
    Button as ZhButton,
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { productTypeOptions } from "@/api/services/prodTypeService";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { useUserStore } from "@/stores/react/authStore";
import { formatTimeEn } from "@/utils/formatter";
import { formatNumber, toThousand } from "@/utils/number";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import TableImageCell from "@/views/components/TableImageCell";
import Fullscreen from "@/views/components/Fullscreen/Fullscreen";
import DataCard from "@/views/components/DataCard/DataCard";
import CollectorsTableDialog from "./CollectorsTableDialog";
import UserGoodsStatisticsDialog from "../components/UserGoodsStatisticsDialog";
import UserGoodsStatisticsForVisitorDialog from "../components/UserGoodsStatisticsForVisitorDialog";
import { applyGoodsOperationTopState, consumeGoodsOperationTopState } from "./topState";

export interface GoodsOperationAnalysisQuery extends Omit<ProductPagingParams, "page" | "size"> {
    page: string;
    size: string;
    beginTime?: string;
    endTime?: string;
}

export interface GoodsOperationAnalysisPageProps {
    loadStatistics?: (params: ProductStatisticAllParams) => Promise<Partial<ProductStatisticAllVo>>;
    loadProducts?: (params: ProductPagingParams & { beginTime?: string; endTime?: string }) => Promise<{
        list?: ProductOperationVo[];
        total?: number;
    }>;
    exportProducts?: (
        params: ProductStatisticAllParams & { showColumns: string[] },
        filename: string
    ) => Promise<unknown>;
    permissions?: string[];
}

/** 商品品类选项沿用 Vue Cascader 的树结构与类型标记。 */
interface GoodsOperationCategoryNode {
    id: string | number;
    name: string;
    type?: number;
    children?: GoodsOperationCategoryNode[];
}

/** 将级联路径压为旧商品接口接受的末级品类 ID 列表。 */
const normalizeCategoryIds = (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    const ids = value.map(item => (Array.isArray(item) ? item[item.length - 1] : item)).filter(Boolean);
    return ids.length ? ids : undefined;
};

/** 复用 Vue 商品运营统计接口，保持 /product/statistic-all 参数契约。 */
export async function loadGoodsOperationStatistics(params: ProductStatisticAllParams) {
    return dashboardService.getProductStatisticAll(params);
}

/** 复用 Vue 商品运营分页接口，保持 /product/paging 的 page/size 字符串格式。 */
export async function loadGoodsOperationProducts(
    params: ProductPagingParams & { beginTime?: string; endTime?: string }
) {
    return dashboardService.getProductPaging(params);
}

/** 将空筛选值从查询参数中剔除，避免改变 Vue 页面原有请求语义。 */
const compactParams = <T extends Record<string, unknown>>(params: T) =>
    Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== "" && value !== null)
    ) as T;

/** 生成 Vue 快捷日期对应的完整时间边界，并保留 scope 供按钮高亮。 */
export function buildGoodsOperationDateQuery(now: Dayjs = dayjs(), scope = 2) {
    if (scope === 1) return { scope };

    const endTime = now.endOf("day");
    const beginTime =
        scope === 6
            ? endTime.startOf("day")
            : scope === 5
              ? endTime.subtract(1, "week").add(1, "day").startOf("day")
              : scope === 3
                ? endTime.subtract(3, "month").add(1, "day").startOf("day")
                : scope === 4
                  ? endTime.subtract(1, "year").add(1, "day").startOf("day")
                  : endTime.subtract(1, "month").add(1, "day").startOf("day");

    return {
        scope,
        beginTime: beginTime.format("YYYY-MM-DDTHH:mm:ss"),
        endTime: endTime.format("YYYY-MM-DDTHH:mm:ss")
    };
}

/** 每次重置都以当前时间重新计算近一月，避免长时间打开页面后日期过期。 */
const createDefaultQuery = (): GoodsOperationAnalysisQuery => ({
    page: "1",
    size: "10",
    ...buildGoodsOperationDateQuery()
});

const toDateRangeValue = (query: Pick<GoodsOperationAnalysisQuery, "beginTime" | "endTime">) =>
    [query.beginTime?.slice(0, 10) || null, query.endTime?.slice(0, 10) || null] as [string | null, string | null];

/** 严格解析日期输入，避免日期控件失焦时把非法文本写回查询条件。 */
export const normalizeGoodsOperationDateInput = (value: string) => {
    const normalizedValue = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) return null;
    const parsedDate = dayjs(normalizedValue);
    return parsedDate.isValid() && parsedDate.format("YYYY-MM-DD") === normalizedValue ? normalizedValue : null;
};

/** 将秒数转换为 Vue DataCard 的“数字 + 中文单位”分段结构。 */
const formatGoodsDurationCard = (value?: number) => {
    const normalizedValue = Number.isFinite(value) ? Number(value) : 0;
    if (normalizedValue > 0 && normalizedValue < 1) {
        return { data: [{ label: "<" }, { value: 1, label: "秒" }], tooltip: undefined };
    }
    const formatted = formartTimeWithUnitAndChinese(normalizedValue);
    return {
        data: formatted.impreciseTokens.length
            ? formatted.impreciseTokens.map(item => ({ value: item.value, label: item.label }))
            : [{ value: 0 }],
        tooltip: formatted.precise
    };
};

const dateScopeOptions = [
    { label: "全部", value: 1 },
    { label: "今日", value: 6 },
    { label: "近一周", value: 5 },
    { label: "近一月", value: 2 },
    { label: "近一季度", value: 3 },
    { label: "近一年", value: 4 }
];

/** 导出字段必须与旧 Vue 明细表的列属性保持一致，避免只导出基础四列。 */
export const GOODS_OPERATION_EXPORT_COLUMNS = [
    "productId",
    "title",
    "productTypeName",
    "supplierName",
    "state",
    "updateTime",
    "avgUserStayTime",
    "avgSingleStayTime",
    "productVisitorCount",
    "productVisitCount",
    "totalSalesAmount",
    "settlementAmount",
    "refundAmount",
    "totalSalesVolume",
    "actualSettledTons",
    "favoriteCount",
    "productType"
] as const;

/** 还原旧 Vue 分析板块标题、日期和蓝色引导线。 */
function AnalysisPanel({
    title,
    date,
    testId,
    children
}: {
    title: string;
    date?: string;
    testId?: string;
    children: ReactNode;
}) {
    return (
        <section className="goods-operation-panel" data-testid={testId}>
            <header className="goods-operation-panel__header">
                <span
                    data-testid={testId ? "goods-operation-panel-title-mark" : undefined}
                    className="goods-operation-panel__mark"
                />
                <span>{title}</span>
                {date ? <span className="goods-operation-panel__date">{date}</span> : null}
            </header>
            {children}
        </section>
    );
}

/** 旧 Vue 分页即使只有一页也显示快速跳转，补齐 Ant 分页的单页隐藏行为。 */
function GoodsOperationQuickJumper({
    current,
    pageSize,
    total,
    onChange
}: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
}) {
    const [value, setValue] = useState(String(current));
    const maxPage = Math.max(1, Math.ceil(total / pageSize));

    useEffect(() => {
        setValue(String(current));
    }, [current]);

    const submit = () => {
        const nextPage = Math.min(maxPage, Math.max(1, Number.parseInt(value, 10) || 1));
        setValue(String(nextPage));
        onChange(nextPage);
    };

    return (
        <span className="goods-operation-quick-jumper">
            前往
            <Input
                aria-label="快速跳转页码"
                value={value}
                onChange={event => setValue(event.target.value.replace(/\D/g, ""))}
                onPressEnter={submit}
            />
            页
        </span>
    );
}

/** 用真实 SVG 结构承载四层转化数据，与旧 Vue 漏斗的颜色和层级一致。 */
function GoodsConversionFunnel({ stages = [] }: { stages?: ProductStatisticAllVo["funnelStages"] }) {
    const colors = ["#0e88ff", "#65c730", "#ffbf00", "#fb6f12"];
    const names = ["访问系统", "访问商品", "确认订单页面", "完成下单"];
    const classNames = ["access-system", "product-visit", "confirm-order", "complete-order"];
    const formatRate = (value?: unknown) =>
        typeof value === "number" ? `${Number.isInteger(value) ? value : value.toFixed(2)}%` : "";

    return (
        <div className="goods-operation-funnel">
            <svg
                role="img"
                aria-label="商品交易转化漏斗"
                viewBox="0 0 311 249"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="goods-operation-funnel__svg"
            >
                <defs>
                    <linearGradient
                        id="goods-funnel-yellow"
                        x1="157.076"
                        y1="82.0105"
                        x2="157.076"
                        y2="199.998"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#fff" />
                        <stop offset="1" stopColor="#fff1c7" />
                    </linearGradient>
                    <linearGradient
                        id="goods-funnel-orange"
                        x1="158.755"
                        y1="174.295"
                        x2="158.755"
                        y2="302.365"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#fff" />
                        <stop offset="1" stopColor="#f6b78e" />
                    </linearGradient>
                    <linearGradient
                        id="goods-funnel-green"
                        x1="155.5"
                        y1="38.6094"
                        x2="155.5"
                        y2="214.282"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#fff" />
                        <stop offset="1" stopColor="#cdf5b8" />
                    </linearGradient>
                    <linearGradient
                        id="goods-funnel-green-line"
                        x1="157.069"
                        y1="139.947"
                        x2="157.069"
                        y2="80.1954"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset=".43184" stopColor="#5ca336" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                        id="goods-funnel-yellow-line"
                        x1="158.757"
                        y1="199"
                        x2="158.757"
                        y2="154.893"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset=".43184" stopColor="#efcb5f" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path
                    opacity=".5"
                    d="M40.8438 91.2031L41.5443 93.1897C48.3981 106.814 96.071 116.023 156.029 116.023C216.565 116.023 265.142 104.929 270.762 92.6078L271.214 91.2031C271.126 91.6728 270.975 92.1412 270.762 92.6078L244.345 174.699C242.765 184.637 217.95 197.973 156.029 199.427C94.1075 200.881 73.155 183.182 70.2882 174.699L41.5443 93.1897C41.2162 92.5374 40.9816 91.8751 40.8438 91.2031Z"
                    fill="url(#goods-funnel-yellow)"
                />
                <path
                    opacity=".5"
                    d="M75 181L75.931 183.19C82.1702 192.719 115.878 199.104 158 199.104C200.743 199.104 235.213 191.334 240.393 182.566L241 181C240.903 181.524 240.699 182.047 240.393 182.566L221.638 230.902C220.5 238.15 202.619 247.878 158 248.938C113.381 249.999 98.2828 237.089 96.2171 230.902L75.931 183.19C75.4643 182.477 75.1513 181.747 75 181Z"
                    fill="url(#goods-funnel-orange)"
                />
                <path
                    opacity=".5"
                    d="M0 38.6094L4.30086 46.7758C18.0506 60.3747 65.3463 75.8914 154.458 75.8914C245.33 75.8914 294.23 59.0943 307.351 45.6482L311 38.6094C311 40.7523 309.797 43.142 307.351 45.6482L271.209 115.375C269.115 125.318 236.559 137.652 154.458 139.107C72.3569 140.562 44.2296 123.862 40.4286 115.375L4.30086 46.7758C1.40794 43.9146 0 41.1384 0 38.6094Z"
                    fill="url(#goods-funnel-green)"
                />
                <path
                    d="M156.021 89.3877C188.072 89.3877 217.061 92.2657 238.012 96.9053C248.495 99.2267 256.916 101.979 262.693 105.004C265.583 106.517 267.766 108.075 269.217 109.645C270.663 111.209 271.334 112.732 271.334 114.208C271.334 115.684 270.663 117.207 269.217 118.771C267.766 120.341 265.583 121.899 262.693 123.412C256.916 126.437 248.495 129.189 238.012 131.511C217.061 136.15 188.072 139.028 156.021 139.028C123.971 139.028 94.9825 136.15 74.0312 131.511C63.5483 129.189 55.1274 126.437 49.3496 123.412C46.4597 121.899 44.2756 120.341 42.8252 118.771C41.3794 117.207 40.708 115.684 40.708 114.208C40.708 112.732 41.3794 111.209 42.8252 109.645C44.2756 108.075 46.4598 106.517 49.3496 105.004C55.1274 101.979 63.5483 99.2267 74.0312 96.9053C94.9825 92.2657 123.971 89.3877 156.021 89.3877Z"
                    stroke="url(#goods-funnel-green-line)"
                    strokeWidth="1.83852"
                />
                <path
                    d="M158 161.919C181.149 161.919 202.078 164.043 217.194 167.462C224.76 169.173 230.82 171.198 234.966 173.415C237.039 174.524 238.589 175.658 239.611 176.787C240.629 177.912 241.081 178.983 241.081 180C241.081 181.017 240.629 182.088 239.611 183.213C238.589 184.342 237.039 185.476 234.966 186.585C230.82 188.802 224.76 190.827 217.194 192.538C202.078 195.957 181.149 198.081 158 198.081C134.851 198.081 113.922 195.957 98.8057 192.538C91.24 190.827 85.1799 188.802 81.0342 186.585C78.961 185.476 77.4107 184.342 76.3887 183.213C75.3714 182.088 74.9189 181.017 74.9189 180C74.9189 178.983 75.3714 177.912 76.3887 176.787C77.4107 175.658 78.961 174.524 81.0342 173.415C85.1799 171.198 91.24 169.173 98.8057 167.462C113.922 164.043 134.851 161.919 158 161.919Z"
                    stroke="url(#goods-funnel-yellow-line)"
                    strokeWidth="1.83852"
                />
                <path
                    d="M154.977 0.918945C197.72 0.918954 236.385 5.23627 264.339 12.2002C278.323 15.6839 289.574 19.8182 297.308 24.373C305.103 28.9645 309.033 33.8051 309.033 38.6084C309.033 43.4119 305.103 48.2531 297.308 52.8447C289.574 57.3996 278.323 61.5338 264.339 65.0176C236.385 71.9815 197.72 76.2988 154.977 76.2988C112.233 76.2988 73.5668 71.9815 45.6133 65.0176C31.6296 61.5339 20.3787 57.3996 12.6455 52.8447C4.84983 48.253 0.918945 43.4119 0.918945 38.6084C0.919176 33.805 4.8501 28.9646 12.6455 24.373C20.3787 19.8182 31.6297 15.6839 45.6133 12.2002C73.5668 5.23623 112.233 0.918945 154.977 0.918945Z"
                    fill="#e4f1ff"
                    stroke="#61a6e9"
                    strokeWidth="1.83852"
                />
                {stages.map((stage, index) => (
                    <g
                        key={`${stage.stageName || names[index]}-${index}`}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`translate(155, ${[45, 120, 180, 230][index]})`}
                    >
                        <text x="-8.5" textAnchor="end" fill={colors[index]} fontSize="24" fontWeight="500">
                            {stage.userCount ?? ""}
                        </text>
                        {index > 0 ? (
                            <text x="8.5" textAnchor="start" fill="#000000a6" fontSize="16" fontWeight="500">
                                {formatRate(stage.conversionRate)}
                            </text>
                        ) : null}
                    </g>
                ))}
            </svg>
            <div className="goods-operation-funnel__legend">
                {names.map((name, index) => {
                    const stage = stages[index];
                    // 旧 Vue 漏斗的图形色和右侧标签色略有不同，分别保留两套颜色契约。
                    const legendColor = index === 3 ? "#f85c25" : colors[index];
                    return (
                        <div
                            key={name}
                            className={`goods-operation-funnel__legend-item ${classNames[index]}`}
                            style={{ borderColor: legendColor }}
                        >
                            <strong style={{ color: legendColor }}>{stage?.stageName || name}</strong>
                            {index ? <small>转化率：{formatRate(stage?.conversionRate)}</small> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** Dashboard 商品运营分析 React 页面，保留 Vue 首屏统计、查询、分页和详情跳转。 */
export function GoodsOperationAnalysisPage({
    loadStatistics = loadGoodsOperationStatistics,
    loadProducts = loadGoodsOperationProducts,
    exportProducts = dashboardService.exportProductOperation.bind(dashboardService),
    permissions
}: GoodsOperationAnalysisPageProps) {
    const [query, setQuery] = useState<GoodsOperationAnalysisQuery>(createDefaultQuery);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const [form] = Form.useForm<
        Pick<GoodsOperationAnalysisQuery, "keyword" | "productTypeId" | "supplierId" | "state" | "productType"> & {
            time?: [string | null, string | null];
        }
    >();
    const [statistics, setStatistics] = useState<Partial<ProductStatisticAllVo>>({});
    const [products, setProducts] = useState<ProductOperationVo[]>([]);
    const [total, setTotal] = useState(0);
    const [categoryOptions, setCategoryOptions] = useState<GoodsOperationCategoryNode[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<Array<{ label: string; value: number | string }>>([]);
    const [supplierLoading, setSupplierLoading] = useState(false);
    // 用 ref 记录供应商请求状态，避免 focus/open/search 同时触发时因异步 state 尚未刷新而重复请求。
    const supplierOptionsLoadedRef = useRef(false);
    const supplierRequestRef = useRef<{ name: string; promise: Promise<void> } | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [exporting, setExporting] = useState(false);
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(() => toDateRangeValue(query));
    const [draftScope, setDraftScope] = useState<number | undefined>(query.scope);
    // 保存当前商品，供旧 Vue 收藏明细弹窗按商品编号加载记录。
    const [collectorsRow, setCollectorsRow] = useState<ProductOperationVo | null>(null);
    // 记录当前统计卡片打开的明细类型，沿用 Vue 的用户/访客两个独立弹窗。
    const [statisticsDetail, setStatisticsDetail] = useState<"user" | "visitor" | null>(null);

    const canRead =
        permissions === undefined || permissions.length === 0 || permissions.includes("ROLE_SAND_PRODUCT_BI_R");
    // 对齐 Vue 搜索栏的 v-auth：导出入口只对拥有独立导出权限的用户开放。
    const canExport = (permissions ?? storePermissions).includes("ROLE_SAND_PRODUCT_BI_EXPORT");

    useEffect(() => {
        let disposed = false;
        // Vue 在筛选栏挂载时预载商品品类树，避免首次打开下拉时出现空选项。
        void productTypeOptions()
            .then(options => {
                if (!disposed) setCategoryOptions(Array.isArray(options) ? options : []);
            })
            .catch(() => {
                if (!disposed) setCategoryOptions([]);
            });
        return () => {
            disposed = true;
        };
    }, []);

    /** 聚焦或展开时懒加载供应商，输入关键字时沿用 Vue SelectRemote 的远程查询。 */
    const loadSupplierOptions = (name = "") => {
        const normalizedName = name.trim();
        if (!normalizedName && supplierOptionsLoadedRef.current) return Promise.resolve();
        if (supplierRequestRef.current?.name === normalizedName) return supplierRequestRef.current.promise;

        setSupplierLoading(true);
        const request = enterpriseManageService
            .selectorByProduct({ name: normalizedName, productType: 1 })
            .then(suppliers => {
                setSupplierOptions(
                    (suppliers || []).map(supplier => ({
                        label: supplier.supplierName || String(supplier.id),
                        value: supplier.id
                    }))
                );
                if (!normalizedName) supplierOptionsLoadedRef.current = true;
            })
            .catch(() => {
                setSupplierOptions([]);
            })
            .finally(() => {
                setSupplierLoading(false);
                if (supplierRequestRef.current?.promise === request) supplierRequestRef.current = null;
            });
        supplierRequestRef.current = { name: normalizedName, promise: request };
        return request;
    };

    /** 按旧 Element 下拉的最长文本宽度计算弹层宽度，避免长供应商名称被截断。 */
    const supplierDropdownWidth = useMemo(() => {
        if (supplierOptions.length === 0 || typeof document === "undefined") return 128;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) return 128;
        context.font = '14px Arial, "Microsoft YaHei", sans-serif';
        const longestLabelWidth = Math.max(
            ...supplierOptions.map(option => context.measureText(String(option.label)).width)
        );
        // 旧 Element 下拉额外保留左右内边距与滚动条空间，最小宽度仍保持筛选控件宽度。
        return Math.max(128, Math.ceil(longestLabelWidth + 52));
    }, [supplierOptions]);

    const pagingParams = useMemo(
        () => compactParams({ ...query, scope: query.scope === 6 ? undefined : query.scope }),
        [query]
    );
    const statisticParams = useMemo(() => {
        // 统计接口不接受分页字段，按 Vue 页面语义只复制非分页参数。
        const params = Object.fromEntries(
            Object.entries(pagingParams).filter(([key]) => key !== "page" && key !== "size")
        );
        return compactParams(params) as ProductStatisticAllParams;
    }, [pagingParams]);

    useEffect(() => {
        if (!canRead) return;
        let disposed = false;
        setErrorMessage("");
        // 统计卡片和明细列表是 Vue 首屏的两条独立请求，保持并行加载以减少等待时间。
        void Promise.all([loadStatistics(statisticParams), loadProducts(pagingParams)])
            .then(([summary, result]) => {
                if (disposed) return;
                setStatistics(summary || {});
                // 置顶详情返回时优先合并本次操作，避免列表接口缓存短暂落后导致图标消失。
                setProducts(applyGoodsOperationTopState(result?.list || [], consumeGoodsOperationTopState()));
                setTotal(result?.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setStatistics({});
                setProducts([]);
                setTotal(0);
                setErrorMessage("商品运营分析加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [canRead, loadProducts, loadStatistics, pagingParams, statisticParams]);

    const submitFilters = (values: Record<string, unknown>) => {
        const [beginDate, endDate] = dateRange;
        const normalizedValues = { ...values, productTypeId: normalizeCategoryIds(values.productTypeId) };
        setQuery({
            page: "1",
            size: "10",
            scope: draftScope,
            ...compactParams({
                ...normalizedValues,
                beginTime: beginDate ? `${beginDate}T00:00:00` : undefined,
                endTime: endDate ? `${endDate}T23:59:59` : undefined
            })
        } as GoodsOperationAnalysisQuery);
    };

    /** 直接编辑日期输入框后同步受控值，防止失焦时回退到编辑前的日期。 */
    const commitDateRangeInput = (event: FocusEvent<HTMLElement>) => {
        const pickerRoot = event.currentTarget.matches("input")
            ? event.currentTarget.closest<HTMLElement>(".ant-picker")
            : event.currentTarget;
        const inputValues = Array.from(pickerRoot?.querySelectorAll<HTMLInputElement>("input") ?? [])
            .slice(0, 2)
            .map(input => normalizeGoodsOperationDateInput(input.value));
        if (inputValues.length !== 2 || inputValues.some(value => value === null)) return;

        const nextRange = inputValues as [string, string];
        setDateRange(nextRange);
        setDraftScope(undefined);
        form.setFieldValue("time", nextRange);
    };

    const resetFilters = () => {
        form.resetFields();
        const defaultQuery = createDefaultQuery();
        setDateRange(toDateRangeValue(defaultQuery));
        setDraftScope(defaultQuery.scope);
        setQuery(defaultQuery);
    };

    /** 快捷日期同时更新请求边界、日期框和标题日期，保持 Vue 的单一筛选状态。 */
    const selectDateScope = (scope: number) => {
        const dateQuery = buildGoodsOperationDateQuery(dayjs(), scope);
        // Vue 搜索项使用 v-model，点击快捷日期会连同尚未点击搜索的当前表单值一起提交。
        const formValues = form.getFieldsValue(["keyword", "productTypeId", "supplierId", "state", "productType"]);
        setDateRange(toDateRangeValue(dateQuery));
        setDraftScope(scope);
        setQuery(current => {
            const next = { ...current, ...formValues };
            delete next.beginTime;
            delete next.endTime;
            return compactParams({ ...next, page: "1", ...dateQuery }) as GoodsOperationAnalysisQuery;
        });
    };

    const exportCurrent = async () => {
        setExporting(true);
        try {
            await exportProducts(
                { ...statisticParams, showColumns: [...GOODS_OPERATION_EXPORT_COLUMNS] },
                `商品运营分析${new Date().toISOString().slice(0, 10).replaceAll("-", "")}`
            );
        } catch {
            setErrorMessage("商品运营分析导出失败");
        } finally {
            setExporting(false);
        }
    };

    // 保留旧 Vue 明细表的 custom sort：排序字段直接使用列 prop，并把分页重置到第一页。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            setQuery(current => {
                const nextQuery = { ...current, page: "1" };
                if (!prop || !order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = prop;
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 明细列沿用 Vue 表格字段和展示规则，避免迁移后改变详情跳转及状态文案。
    const productColumns: TableColumnProps<ProductOperationVo>[] = [
        {
            label: "商品编号",
            minWidth: 180,
            prop: "productId",
            render: ({ row }) =>
                row.productType === 1 ? (
                    <Link to={`/dashboard/consignmentGoodsAnalysisDetail?id=${row.productId}`}>{row.productId}</Link>
                ) : (
                    row.productId || "--"
                )
        },
        {
            label: "主图/标题",
            minWidth: 220,
            prop: "title",
            render: ({ row }) => (
                <div className="goods-operation-product-cell">
                    <TableImageCell
                        alt="商品主图"
                        src={row.mainFile?.path}
                        // 悬浮提示只展示完整商品标题，避免把可视区域的置顶图标一并渲染到提示中。
                        tooltipTitle={row.title || "--"}
                        title={
                            <span className="goods-operation-product-title">
                                <span>{row.title || "--"}</span>
                                {row.isTop ? <i aria-label="置顶" className="iconfont icon-a-zhiding1" /> : null}
                            </span>
                        }
                    />
                </div>
            )
        },
        { label: "品类", minWidth: 220, prop: "productTypeName" },
        { label: "供应商", minWidth: 200, prop: "supplierName" },
        {
            label: "商品状态",
            minWidth: 90,
            prop: "state",
            render: ({ row }) => (row.state === 1 ? "上架" : row.state === 2 ? "下架" : (row.state ?? "--"))
        },
        {
            label: "更新时间",
            minWidth: 155,
            prop: "updateTime",
            sortable: "custom",
            render: ({ row }) => (row.updateTime ? dayjs(row.updateTime).format("YYYY-MM-DD HH:mm") : "--")
        },
        {
            label: "平均用户停留时长",
            minWidth: 160,
            prop: "avgUserStayTime",
            sortable: "custom",
            render: ({ row }) => formatTimeEn(row.avgUserStayTime)
        },
        {
            label: "平均单次停留时长",
            minWidth: 160,
            prop: "avgSingleStayTime",
            sortable: "custom",
            render: ({ row }) => formatTimeEn(row.avgSingleStayTime)
        },
        {
            label: "商品访问人数",
            minWidth: 130,
            prop: "productVisitorCount",
            sortable: "custom",
            render: ({ row }) => toThousand(row.productVisitorCount)
        },
        {
            label: "商品访问次数",
            minWidth: 140,
            prop: "productVisitCount",
            sortable: "custom",
            render: ({ row }) => toThousand(row.productVisitCount)
        },
        {
            label: "总销售额(元)",
            minWidth: 150,
            prop: "totalSalesAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.totalSalesAmount, 2)
        },
        {
            label: "结算金额(元)",
            minWidth: 150,
            prop: "settlementAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.settlementAmount, 2)
        },
        {
            label: "退款金额(元)",
            minWidth: 130,
            prop: "refundAmount",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.refundAmount, 2)
        },
        {
            label: "总销量(吨)",
            minWidth: 120,
            prop: "totalSalesVolume",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.totalSalesVolume, 2)
        },
        {
            label: "实际结算吨数(吨)",
            minWidth: 150,
            prop: "actualSettledTons",
            sortable: "custom",
            render: ({ row }) => formatNumber(row.actualSettledTons, 2)
        },
        {
            label: "收藏人数",
            width: 100,
            prop: "favoriteCount",
            render: ({ row }) =>
                row.favoriteCount ? (
                    <Button
                        type="link"
                        aria-label={`查看收藏明细 ${row.favoriteCount}`}
                        onClick={() => setCollectorsRow(row)}
                    >
                        {row.favoriteCount}
                    </Button>
                ) : (
                    row.favoriteCount
                )
        },
        {
            label: "商品类型",
            minWidth: 150,
            prop: "productType",
            render: ({ row }) => PRODUCT_TYPE.getIntroByCode(row.productType) || "--"
        }
    ];

    if (!canRead) {
        return <Alert aria-label="goods-operation-analysis-page" message="暂无商品运营分析权限" type="warning" />;
    }

    const dateLabel =
        query.beginTime && query.endTime ? `${query.beginTime.slice(0, 10)}~${query.endTime.slice(0, 10)}` : "";
    const totalSalesAmount = formatNumberWithUnitAndChinese(Number(statistics.totalSalesAmount) || 0, "元");
    const settlementAmount = formatNumberWithUnitAndChinese(Number(statistics.settlementAmount) || 0, "元");
    const refundAmount = formatNumberWithUnitAndChinese(Number(statistics.refundAmount) || 0, "元");
    const totalSalesVolume = formatNumberWithUnitAndChinese(Number(statistics.totalSalesVolume) || 0);
    const actualSettledTons = formatNumberWithUnitAndChinese(Number(statistics.actualSettledTons) || 0);
    const productVisitorCount = formatNumberWithUnitAndChinese(Number(statistics.productVisitorCount) || 0);
    const productVisitCount = formatNumberWithUnitAndChinese(Number(statistics.productVisitCount) || 0);
    const avgUserStayTime = formatGoodsDurationCard(statistics.avgUserStayTime);
    const avgSingleStayTime = formatGoodsDurationCard(statistics.avgSingleStayTime);

    return (
        <section
            id="goods-operation-analysis-content"
            aria-label="goods-operation-analysis-page"
            className="goods-operation-analysis-page flex min-h-full flex-col"
        >
            <section className="goods-operation-toolbar">
                <h1 className="goods-operation-title">商品运营分析</h1>
                <div className="goods-operation-toolbar__main">
                    <Form
                        className="admin-filter-form goods-operation-filter-form"
                        form={form}
                        layout="inline"
                        onFinish={submitFilters}
                    >
                        {canExport ? (
                            <ZhButton
                                className="goods-operation-export-button"
                                color="primary"
                                loading={exporting}
                                onClick={exportCurrent}
                            >
                                导出
                            </ZhButton>
                        ) : null}
                        <Form.Item className="goods-operation-filter-form__keyword" name="keyword">
                            <Input
                                aria-label="商品编号或商品标题"
                                placeholder="请输入商品编号或商品标题"
                                autoComplete="off"
                                allowClear
                                // Element 旧版清除按钮会立即触发查询，不能只清空表单值。
                                onClear={() => submitFilters({ ...form.getFieldsValue(), keyword: undefined })}
                            />
                        </Form.Item>
                        <Form.Item className="goods-operation-filter-form__category" name="productTypeId">
                            <Cascader
                                aria-label="品类"
                                placeholder="请选择品类"
                                multiple
                                showSearch
                                maxTagCount={1}
                                options={categoryOptions}
                                fieldNames={{ label: "name", value: "id", children: "children" }}
                                popupClassName="goods-operation-filter-dropdown"
                                onChange={value => {
                                    if (!value?.length) {
                                        submitFilters({ ...form.getFieldsValue(), productTypeId: undefined });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="goods-operation-filter-form__supplier" name="supplierId">
                            <Select
                                aria-label="供应商"
                                placeholder="请选择供应商"
                                allowClear
                                loading={supplierLoading}
                                options={supplierOptions}
                                showSearch
                                filterOption={false}
                                listHeight={260}
                                popupMatchSelectWidth={supplierDropdownWidth}
                                popupClassName="goods-operation-filter-dropdown"
                                onFocus={() => void loadSupplierOptions()}
                                onSearch={value => void loadSupplierOptions(value)}
                                onOpenChange={open => {
                                    if (open) void loadSupplierOptions();
                                }}
                                onChange={value => {
                                    if (value === undefined) {
                                        submitFilters({ ...form.getFieldsValue(), supplierId: undefined });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="goods-operation-filter-form__state" name="state">
                            <Select
                                aria-label="商品状态"
                                placeholder="请选择商品状态"
                                allowClear
                                style={{ width: "100%" }}
                                options={[
                                    { label: "上架", value: 1 },
                                    { label: "下架", value: 2 }
                                ]}
                                popupClassName="goods-operation-filter-dropdown"
                                onChange={value => {
                                    if (value === undefined) {
                                        submitFilters({ ...form.getFieldsValue(), state: undefined });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="goods-operation-filter-form__type" name="productType">
                            <Select
                                aria-label="商品类型"
                                placeholder="请选择商品类型"
                                allowClear
                                style={{ width: "100%" }}
                                options={PRODUCT_TYPE.options.map(option => ({
                                    label: option.label,
                                    value: option.value
                                }))}
                                popupClassName="goods-operation-filter-dropdown"
                                onChange={value => {
                                    if (value === undefined) {
                                        submitFilters({ ...form.getFieldsValue(), productType: undefined });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="goods-operation-filter-form__date">
                            <ZhDateRangePicker
                                aria-label="商品运营日期范围"
                                rootClassName="admin-filter-date-picker"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="开始日期"
                                endPlaceholder="结束日期"
                                type="daterange"
                                clearable={false}
                                value={dateRange}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_value, formattedValue) => {
                                    const nextRange: [string | null, string | null] = formattedValue
                                        ? [formattedValue[0] || null, formattedValue[1] || null]
                                        : [null, null];
                                    setDateRange(nextRange);
                                    setDraftScope(undefined);
                                    form.setFieldValue("time", nextRange);
                                }}
                                onBlur={commitDateRangeInput}
                            />
                        </Form.Item>
                        <ZhButton
                            className="goods-operation-search-button"
                            aria-label="搜索"
                            color="primary"
                            htmlType="submit"
                            icon={<TableSearchIcon name="search" />}
                        >
                            搜索
                        </ZhButton>
                    </Form>
                    <Fullscreen targetId="goods-operation-analysis-content" />
                    <button
                        type="button"
                        className="goods-operation-refresh-button"
                        aria-label="刷新商品运营分析"
                        onClick={resetFilters}
                    >
                        <i aria-hidden="true" className="iconfont icon-Frame8" />
                    </button>
                </div>
                <div className="goods-operation-date-scopes" role="group" aria-label="快捷日期">
                    {dateScopeOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={draftScope === option.value}
                            className={draftScope === option.value ? "is-active" : undefined}
                            onClick={() => selectDateScope(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </section>
            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
            <div className="goods-operation-content">
                <div className="goods-operation-data-panels">
                    <AnalysisPanel title="商品运营统计" date={dateLabel} testId="goods-operation-statistics-panel">
                        <div className="goods-operation-statistics-grid">
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看总销售额用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title={`总销售额(${totalSalesAmount.baseUnit}元)`}
                                    data={totalSalesAmount.imprecise}
                                    tooltipContent={`${totalSalesAmount.precise} 元`}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看结算金额用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title={`结算金额(${settlementAmount.baseUnit}元)`}
                                    tooltipContent={`${settlementAmount.precise} 元`}
                                >
                                    <div className="goods-operation-settlement-content">
                                        <div className="goods-operation-settlement-primary">
                                            {settlementAmount.imprecise}
                                        </div>
                                        <div className="goods-operation-settlement-refund">
                                            <span>退款金额</span>
                                            <strong>{refundAmount.imprecise}</strong>
                                            <i>{refundAmount.baseUnit}元</i>
                                        </div>
                                    </div>
                                </DataCard>
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看总销量用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title={`总销量(${totalSalesVolume.baseUnit}吨)`}
                                    data={totalSalesVolume.imprecise}
                                    tooltipContent={`${totalSalesVolume.precise} 吨`}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看实际结算吨数用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title={`实际结算吨数(${actualSettledTons.baseUnit}吨)`}
                                    data={actualSettledTons.imprecise}
                                    tooltipContent={`${actualSettledTons.precise} 吨`}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看商品访问人数访客明细"
                                onClick={() => setStatisticsDetail("visitor")}
                            >
                                <DataCard
                                    title={`商品访问人数${productVisitorCount.baseUnit ? `(${productVisitorCount.baseUnit})` : ""}`}
                                    data={productVisitorCount.imprecise}
                                    tooltipContent={productVisitorCount.precise}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看商品访问次数用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title={`商品访问次数${productVisitCount.baseUnit ? `(${productVisitCount.baseUnit})` : ""}`}
                                    data={productVisitCount.imprecise}
                                    tooltipContent={productVisitCount.precise}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看平均用户停留时长用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title="平均用户停留时长"
                                    data={avgUserStayTime.data}
                                    tooltipContent={avgUserStayTime.tooltip}
                                />
                            </button>
                            <button
                                type="button"
                                className="goods-operation-statistics-button"
                                aria-label="查看平均单次停留时长用户明细"
                                onClick={() => setStatisticsDetail("user")}
                            >
                                <DataCard
                                    title="平均单次停留时长"
                                    data={avgSingleStayTime.data}
                                    tooltipContent={avgSingleStayTime.tooltip}
                                />
                            </button>
                        </div>
                    </AnalysisPanel>
                    <AnalysisPanel title="商品交易转化" date={dateLabel}>
                        <GoodsConversionFunnel stages={statistics.funnelStages} />
                    </AnalysisPanel>
                </div>
                <AnalysisPanel title="商品运营明细" date={dateLabel}>
                    <ZhTable<ProductOperationVo>
                        rowKey={row => String(row.productId)}
                        data={products}
                        columns={productColumns}
                        customColumn
                        customColumnPageCacheKey="GoodsOperationAnalysisTable"
                        pagination={false}
                        onSortChange={handleSortChange}
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    className="self-end"
                                    current={Number(query.page)}
                                    pageSize={Number(query.size)}
                                    total={total}
                                    showSizeChanger={true}
                                    showQuickJumper={true}
                                    onChange={(page, size) =>
                                        setQuery(current => ({ ...current, page: String(page), size: String(size) }))
                                    }
                                />
                                {total > 0 && total <= Number(query.size) ? (
                                    <GoodsOperationQuickJumper
                                        current={Number(query.page)}
                                        pageSize={Number(query.size)}
                                        total={total}
                                        onChange={page => setQuery(current => ({ ...current, page: String(page) }))}
                                    />
                                ) : null}
                            </div>
                        }
                    />
                </AnalysisPanel>
            </div>
            <CollectorsTableDialog
                open={collectorsRow !== null}
                productId={collectorsRow?.productId || ""}
                title={collectorsRow?.title}
                onClose={() => setCollectorsRow(null)}
            />
            <UserGoodsStatisticsDialog
                open={statisticsDetail === "user"}
                baseParams={statisticParams}
                onClose={() => setStatisticsDetail(null)}
            />
            <UserGoodsStatisticsForVisitorDialog
                open={statisticsDetail === "visitor"}
                baseParams={statisticParams}
                onClose={() => setStatisticsDetail(null)}
            />
        </section>
    );
}

export default GoodsOperationAnalysisPage;
